// Minimal hash router over the pages listed in SPEC 8.3.
import type { AppContext } from "./context";
import { setActiveNav } from "./ui/shell";
import { html, errorText } from "./ui/dom";

export type PageRenderer = (root: HTMLElement, ctx: AppContext, params: URLSearchParams) => Promise<void>;

const pages: Record<string, () => Promise<{ render: PageRenderer }>> = {
  "": () => import("./pages/home"),
  servers: () => import("./pages/servers"),
  services: () => import("./pages/services"),
  policy: () => import("./pages/policy-editor"),
  test: () => import("./pages/test-as-user"),
  users: () => import("./pages/user-orgs"),
  audit: () => import("./pages/audit"),
  setup: () => import("./pages/setup")
};

/** `#/services?server=…&service=…` */
export function href(page: string, params: Record<string, string> = {}): string {
  const query = new URLSearchParams(params).toString();
  return `#/${page}${query ? `?${query}` : ""}`;
}

export function route(content: HTMLElement, ctx: AppContext): void {
  // One delegated handler for every `data-goto="#/…"` control, on every page.
  content.addEventListener("click", (event) => {
    const target = (event.target as HTMLElement | null)?.closest<HTMLElement>("[data-goto]");
    if (target?.dataset.goto) location.hash = target.dataset.goto;
  });

  let generation = 0;
  const render = async () => {
    const mine = ++generation;
    const [name, query = ""] = location.hash.replace(/^#\/?/, "").split("?");
    const loader = pages[name] ?? pages[""];
    setActiveNav(`#/${pages[name] ? name : ""}`);
    try {
      const page = await loader();
      // A second navigation while this page was loading wins.
      if (mine !== generation) return;
      await page.render(content, ctx, new URLSearchParams(query));
    } catch (e) {
      if (mine !== generation) return;
      console.error(e);
      content.innerHTML = html`
        <calcite-panel heading="Something went wrong">
          <calcite-notice open kind="danger" width="full">
            <div slot="title">This page could not be loaded</div>
            <div slot="message">${errorText(e)}</div>
          </calcite-notice>
        </calcite-panel>
      `;
    }
  };
  addEventListener("hashchange", render);
  void render();
}
